"use client";

import { useMemo, useState } from "react";
import {
  LifeBuoy,
  MessageSquare,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Search,
  Plus,
  Send,
  Bot,
  ArrowLeft,
  X,
  Calendar,
  User,
  Clock,
  Check,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";

import NewTicketModal from "@/components/user/NewTicketModal";
import { useQuery, useMutation } from "@tanstack/react-query";
import { makeRequest } from "@/utils/makeRequest";
import Loader from "@/components/loader/Loader";
import { queryClient } from "@/services/queryClient";
import { useAppSelector } from "@/store/hooks";
import { useNavigate } from "react-router-dom";
import { ErrorState } from "@/components/ErrorState";
import { useMyTickets } from "@/hooks";
import {
  SupportPriority,
  SupportStatus,
  type SupportTicket,
} from "@/interfaces/ticket";
import { format } from "timeago.js";

export default function UserSupport() {
  const [search, setSearch] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showInfo, setShowInfo] = useState(false);
  const itemsPerPage = 8;

  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id;

  // Fetch tickets list
  const {
    data: ticketsData,
    isLoading,
    isError,
    refetch,
  } = useMyTickets(userId!);

  const tickets = ticketsData?.data ?? [];

  console.log("TICKETS", ticketsData);

  // const testRateLimit = async () => {
  //   try {
  //     const res = await makeRequest.get("/ticket/me");
  //   } catch (error) {
  //     toast.error(getErrorMessage(error));
  //     console.log(error);
  //   }
  // };

  const filteredTickets = useMemo(() => {
    return tickets.filter(
      (t) =>
        t.id.toLowerCase().includes(search.toLowerCase()) ||
        t.subject.toLowerCase().includes(search.toLowerCase()),
    );
  }, [tickets, search]);

  // Pagination
  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);
  const paginatedTickets = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredTickets.slice(start, start + itemsPerPage);
  }, [filteredTickets, currentPage]);

  // Stats
  const openCount = tickets.filter((t) => t.status === "OPEN").length;
  const pendingCount = tickets.filter((t) => t.status === "IN_PROGRESS").length;
  const resolvedCount = tickets.filter((t) => t.status === "RESOLVED").length;

  // Fetch ticket details
  const { data: ticketDetail, isLoading: isDetailLoading } = useQuery({
    queryKey: ["ticketDetail", selectedTicketId],
    enabled: !!selectedTicketId,
    queryFn: async () => {
      const res = await makeRequest.get(`/ticket/${selectedTicketId}`);
      return res.data as SupportTicket;
    },
    staleTime: 1000 * 60,
  });

  // Send reply mutation
  const sendReplyMutation = useMutation({
    mutationFn: async (message: string) => {
      if (!selectedTicketId) throw new Error("No ticket selected");
      await makeRequest.post(`/ticket/${selectedTicketId}/reply`, { message });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["ticketDetail", selectedTicketId],
      });
      setReplyMessage("");
    },
  });

  const handleTicketCreated = () => {
    queryClient.invalidateQueries({ queryKey: ["userTickets", userId] });
    setCurrentPage(1);
  };

  const handleRowClick = (ticketId: string) => {
    setSelectedTicketId(ticketId);
  };

  const closeModal = () => {
    setSelectedTicketId(null);
    setReplyMessage("");
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case SupportStatus.OPEN:
        return (
          <span className="px-2 py-1 rounded text-xs bg-primary/10 text-primary flex items-center gap-1">
            <Clock className="w-3 h-3" /> Open
          </span>
        );
      case SupportStatus.IN_PROGRESS:
        return (
          <span className="px-2 py-1 rounded text-xs bg-blue-500/10 text-blue-500 flex items-center gap-1">
            <MessageSquare className="w-3 h-3" /> In Progress
          </span>
        );
      case SupportStatus.RESOLVED:
        return (
          <span className="px-2 py-1 rounded text-xs bg-emerald-500/10 text-emerald-500 flex items-center gap-1">
            <Check className="w-3 h-3" /> Resolved
          </span>
        );
      case SupportStatus.CLOSED:
        return (
          <span className="px-2 py-1 rounded text-xs bg-muted text-muted-foreground flex items-center gap-1">
            <X className="w-3 h-3" /> Closed
          </span>
        );
      default:
        return <span className="text-muted-foreground">{status}</span>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case SupportPriority.URGENT:
        return (
          <span className="px-2 py-1 rounded text-xs bg-red-500/10 text-red-500">
            Urgent
          </span>
        );
      case SupportPriority.HIGH:
        return (
          <span className="px-2 py-1 rounded text-xs bg-amber-500/10 text-amber-500">
            High
          </span>
        );
      case SupportPriority.MEDIUM:
        return (
          <span className="px-2 py-1 rounded text-xs bg-blue-500/10 text-blue-500">
            Medium
          </span>
        );
      case SupportPriority.LOW:
        return (
          <span className="px-2 py-1 rounded text-xs bg-muted text-muted-foreground">
            Low
          </span>
        );
      default:
        return <span className="text-muted-foreground">{priority}</span>;
    }
  };

  if (isLoading) return <Loader type="bounce" />;

  if (isError)
    return (
      <ErrorState
        onRetry={() => {
          refetch();
        }}
      />
    );

  return (
    <div className="min-h-screen bg-background text-foreground mt-10">
      <main className="flex-1 sm:p-6 lg:p-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 hidden md:flex"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* HEADER */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <LifeBuoy className="w-8 h-8 text-primary" />
              Support Center
            </h1>
            <p className="text-muted-foreground mt-1">
              Professional assistance • Average response time: &lt; 4 hours
            </p>
          </div>
          {/* 
          <Button onClick={testRateLimit} size="lg">
            <Plus className="w-4 h-4 mr-2" />
            Rate Limit Test
          </Button> */}
          <Button onClick={() => setIsCreateOpen(true)} size="lg">
            <Plus className="w-4 h-4 mr-2" />
            New Support Ticket
          </Button>
        </div>

        <Tabs defaultValue="tickets" className="space-y-6">
          <TabsList className="flex gap-2 bg-transparent p-0">
            <TabsTrigger value="tickets">My Tickets</TabsTrigger>
            <TabsTrigger value="faq">FAQ</TabsTrigger>
            {/* <TabsTrigger value="live">Live Support</TabsTrigger> */}
          </TabsList>

          {/* TICKETS TAB */}
          <TabsContent value="tickets" className="space-y-6">
            {/* STATS */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6 flex justify-between items-start">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Open Tickets
                    </p>
                    <h3 className="text-3xl font-bold mt-1 text-orange-600">
                      {openCount}
                    </h3>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-orange-600" />
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6 flex justify-between items-start">
                  <div>
                    <p className="text-sm text-muted-foreground">In Progress</p>
                    <h3 className="text-3xl font-bold mt-1 text-blue-600">
                      {pendingCount}
                    </h3>
                  </div>
                  <Clock3 className="w-8 h-8 text-blue-600" />
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6 flex justify-between items-start">
                  <div>
                    <p className="text-sm text-muted-foreground">Resolved</p>
                    <h3 className="text-3xl font-bold mt-1 text-emerald-600">
                      {resolvedCount}
                    </h3>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </CardContent>
              </Card>
            </div>

            {/* SEARCH */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-3 text-muted-foreground" />
              <Input
                placeholder="Search by Ticket ID or Subject..."
                className="pl-10"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* TABLE */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-6 py-4 text-left font-medium">
                          Ticket ID
                        </th>
                        <th className="px-6 py-4 text-left font-medium">
                          Subject
                        </th>
                        <th className="px-6 py-4 text-left font-medium">
                          Category
                        </th>
                        <th className="px-6 py-4 text-left font-medium">
                          Priority
                        </th>
                        <th className="px-6 py-4 text-left font-medium">
                          Status
                        </th>
                        <th className="px-6 py-4 text-left font-medium">
                          Last Updated
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedTickets.length === 0 ? (
                        <tr>
                          <td
                            colSpan={6}
                            className="text-center py-16 text-muted-foreground"
                          >
                            No tickets found.
                          </td>
                        </tr>
                      ) : (
                        paginatedTickets.map((t) => (
                          <tr
                            key={t.id}
                            onClick={() => handleRowClick(t.id)}
                            className="border-b hover:bg-muted/50 cursor-pointer transition-colors"
                          >
                            <td className="px-6 py-4 font-mono text-primary">
                              {t.id.slice(0, 8)}...
                            </td>
                            <td className="px-6 py-4 max-w-xs truncate">
                              {t.subject}
                            </td>
                            <td className="px-6 py-4 capitalize">
                              {t.category}
                            </td>
                            <td className="text-center py-4 px-4">
                              {getPriorityBadge(t.priority)}
                            </td>
                            <td className="text-center py-4 px-4">
                              {getStatusBadge(t.status)}
                            </td>
                            <td className="px-6 py-4 text-muted-foreground text-sm">
                              {format(t.updatedAt)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                      {Math.min(
                        currentPage * itemsPerPage,
                        filteredTickets.length,
                      )}{" "}
                      of {filteredTickets.length} tickets
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCurrentPage((p) => Math.max(1, p - 1))
                        }
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCurrentPage((p) => Math.min(totalPages, p + 1))
                        }
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FAQ & Live Support tabs remain unchanged */}
          <TabsContent value="faq">
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  q: "How long does dispute resolution take?",
                  a: "Typically within 24–48 hours depending on complexity.",
                },
                {
                  q: "Why is my KYC pending?",
                  a: "Ensure documents are clear and match your identity.",
                },
                {
                  q: "How do I cancel a trade?",
                  a: "Trades auto-cancel after payment window expires.",
                },
                {
                  q: "Can I change payment method mid-trade?",
                  a: "No, payment methods are locked after trade starts.",
                },
              ].map((faq, i) => (
                <Card key={i}>
                  <CardContent className="pt-6 space-y-2">
                    <p className="font-semibold">{faq.q}</p>
                    <p className="text-sm text-muted-foreground">{faq.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* <TabsContent value="live">
            <div className="rounded-2xl border bg-card p-6 space-y-6">
              <div className="flex justify-between">
                <div>
                  <h3 className="font-semibold text-lg">Live Chat Support</h3>
                  <p className="text-sm text-muted-foreground">
                    Connect instantly with our support specialists
                  </p>
                </div>
                <Bot className="text-primary" />
              </div>

              <div className="border rounded-xl p-4 h-80 flex items-center justify-center text-muted-foreground bg-muted/30">
                Live chat system (WebSocket integration ready)
              </div>

              <div className="flex gap-2">
                <Input placeholder="Type your message here..." />
                <Button>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </TabsContent> */}
        </Tabs>
      </main>

      {/* TICKET DETAILS MODAL - Fintech Grade */}
      {selectedTicketId && ticketDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden shadow-xl border border-border">
            {/* HEADER */}
            <div className="p-5 border-b border-border flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Ticket Details
                </h3>
                <p className="text-sm text-muted-foreground">
                  {ticketDetail.id} • {ticketDetail.subject}
                </p>

                <div className="flex gap-2 mt-2">
                  <span className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground">
                    {ticketDetail.status}
                  </span>
                  <span className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground">
                    {ticketDetail.priority}
                  </span>
                  <span className="text-xs px-2 py-1 rounded bg-muted text-muted-foreground">
                    {ticketDetail.category}
                  </span>
                </div>
              </div>

              <button
                onClick={() => setSelectedTicketId(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* BODY (MESSAGES) */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-background">
              {ticketDetail.replies?.length === 0 && (
                <div className="text-center text-muted-foreground text-sm py-10">
                  No replies yet. Support will respond soon.
                </div>
              )}

              {ticketDetail.replies?.map((reply: any) => (
                <div
                  key={reply.id}
                  className={`flex ${
                    reply.repliedById === userId
                      ? "justify-end"
                      : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-4 py-3 text-sm border ${
                      reply.repliedById === userId
                        ? "bg-primary text-primary-foreground border-transparent"
                        : "bg-muted text-foreground"
                    }`}
                  >
                    <div className="text-[11px] opacity-70 mb-1 flex gap-2">
                      <span>
                        {reply.repliedById === userId ? "You" : "Support"}
                      </span>
                      <span>•</span>
                      <span>{new Date(reply.createdAt).toLocaleString()}</span>
                    </div>

                    <p className="whitespace-pre-wrap leading-relaxed">
                      {reply.text}
                    </p>

                    {/* optional attachments */}
                    {reply.images?.length > 0 && (
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {reply.images.map((img: string, i: number) => (
                          <img
                            key={i}
                            src={img}
                            className="w-16 h-16 rounded object-cover border border-border"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* FOOTER (INFO + ACTIONS) */}
            <div className="border-t border-border p-4 bg-card">
              {/* LAST ACTIVITY */}
              <div className="text-xs text-muted-foreground mb-3">
                Last updated:{" "}
                {ticketDetail.replies?.length
                  ? new Date(
                      ticketDetail.replies[ticketDetail.replies.length - 1]
                        ?.createdAt,
                    ).toLocaleString()
                  : new Date(ticketDetail.createdAt).toLocaleString()}
              </div>

              {/* ACTION BUTTONS */}
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedTicketId(null)}
                  className="flex-1 py-2 border border-border rounded-lg hover:bg-muted transition"
                >
                  Close
                </button>

                {ticketDetail.status !== "RESOLVED" && (
                  <button className="flex-1 py-2 bg-primary text-primary-foreground rounded-lg hover:brightness-110">
                    Request Closure
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NEW TICKET MODAL */}
      <NewTicketModal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={handleTicketCreated}
      />
    </div>
  );
}
